import React from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";

import { useEditEntityMutation } from "@api/entity.api";
import { useEntity } from "@components/entity/useEntity";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VStack } from "@design-system/VStack/VStack";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { EditModalFooter } from "./EditModalFooter";

const updateDescriptionFormSchema = z.object({
    description: z.string(),
});

type UpdateDescriptionFormValues = z.infer<typeof updateDescriptionFormSchema>;

const fields = createFormikFields<UpdateDescriptionFormValues>();

interface Props {
    show: boolean;
    onClose(): void;
}

export const UpdateDescriptionModal: React.FC<Props> = ({ show, onClose }) => {
    const entity = useEntity();
    const editEntity = useEditEntityMutation(entity);
    const { error } = useToaster();

    const formik = useFormik<UpdateDescriptionFormValues>({
        initialValues: {
            description: entity.profile?.description ?? "",
        },
        validate: validateFormikWithZodSchema(updateDescriptionFormSchema),
        onSubmit: submitHelper({
            handler: async ({ description }) => {
                try {
                    await editEntity.mutateAsync({
                        profile: { ...entity.profile, description },
                    });
                    onClose();
                } catch {
                    error("Failed to update profile");
                }
            },
        }),
    });

    return (
        <PlainModal show={show} onHide={onClose}>
            <StandardModalHeader>Update Description</StandardModalHeader>
            <FormikProvider value={formik}>
                <Form>
                    <VStack gap={16}>
                        <fields.TextArea
                            name="description"
                            label="Description"
                            showErrorMessage={false}
                            size="xl"
                            rows={5}
                        />
                        <EditModalFooter
                            onClose={onClose}
                            isValid={formik.isValid}
                            isSubmitting={formik.isSubmitting}
                        />
                    </VStack>
                </Form>
            </FormikProvider>
        </PlainModal>
    );
};
